import trainersSeed from '@/pages/centrumSzkolen/data/trainers.json'

const normalizeText = value => `${value ?? ''}`.trim()

const getRelationId = value => {
	if (!value) return ''
	if (typeof value === 'object') {
		return normalizeText(value?._id || value?.id)
	}
	return normalizeText(value)
}

const getRelationName = (value, fallback = '') => {
	const normalizedFallback = normalizeText(fallback)
	if (normalizedFallback) return normalizedFallback

	if (value && typeof value === 'object') {
		return normalizeText(value?.nazwa || value?.name)
	}

	return typeof value === 'string' ? normalizeText(value) : ''
}

const normalizeTrainer = trainer => {
	const normalizedTrainer = trainer && typeof trainer === 'object' ? trainer : {}
	const miastoRelation = normalizedTrainer?.id_miasto || null
	const wojewodztwoRelation =
		normalizedTrainer?.id_wojewodztwo
		|| normalizedTrainer?.id_miasto?.id_wojewodztwo
		|| normalizedTrainer?.id_powiat?.id_wojewodztwo
		|| null
	const secondaryMiastoRelation = normalizedTrainer?.secondary_id_miasto || null
	const secondaryWojewodztwoRelation =
		normalizedTrainer?.secondary_id_wojewodztwo
		|| normalizedTrainer?.secondary_id_miasto?.id_wojewodztwo
		|| normalizedTrainer?.secondary_id_powiat?.id_wojewodztwo
		|| null
	const certificationIds = Array.isArray(normalizedTrainer?.certificationIds)
		? normalizedTrainer.certificationIds.filter(Boolean)
		: []
	const city = getRelationName(miastoRelation, normalizedTrainer?.city)
	const wojewodztwo = getRelationName(wojewodztwoRelation, normalizedTrainer?.wojewodztwo)
	const address = normalizeText(normalizedTrainer?.address)
	const postalCode = normalizeText(normalizedTrainer?.postalCode)
	const secondaryCity = getRelationName(secondaryMiastoRelation, normalizedTrainer?.secondaryCity)
	const secondaryWojewodztwo = getRelationName(secondaryWojewodztwoRelation, normalizedTrainer?.secondaryWojewodztwo)
	const secondaryAddress = normalizeText(normalizedTrainer?.secondaryAddress)
	const secondaryPostalCode = normalizeText(normalizedTrainer?.secondaryPostalCode)
	const idMiasto = getRelationId(miastoRelation)
	const idWojewodztwo = getRelationId(wojewodztwoRelation)
	const secondaryIdMiasto = getRelationId(secondaryMiastoRelation)
	const secondaryIdWojewodztwo = getRelationId(secondaryWojewodztwoRelation)
	const activeAddressType = normalizedTrainer?.activeAddressType === 'secondary'
		&& secondaryCity
		&& secondaryWojewodztwo
		&& secondaryAddress
		&& secondaryPostalCode
		? 'secondary'
		: 'primary'

	return {
		...normalizedTrainer,
		_id: normalizeText(normalizedTrainer?._id || normalizedTrainer?.id),
		name: normalizeText(normalizedTrainer?.name) || '—',
		city,
		wojewodztwo,
		address,
		postalCode,
		secondaryCity,
		secondaryWojewodztwo,
		secondaryAddress,
		secondaryPostalCode,
		activeAddressType,
		activeCity: activeAddressType === 'secondary' ? secondaryCity : city,
		activeWojewodztwo: activeAddressType === 'secondary' ? secondaryWojewodztwo : wojewodztwo,
		activeAddress: activeAddressType === 'secondary' ? secondaryAddress : address,
		activePostalCode: activeAddressType === 'secondary' ? secondaryPostalCode : postalCode,
		active_id_miasto: activeAddressType === 'secondary' ? secondaryIdMiasto : idMiasto,
		active_id_wojewodztwo: activeAddressType === 'secondary' ? secondaryIdWojewodztwo : idWojewodztwo,
		id_miasto: idMiasto,
		id_wojewodztwo: idWojewodztwo,
		secondary_id_miasto: secondaryIdMiasto,
		secondary_id_wojewodztwo: secondaryIdWojewodztwo,
		defaultHourlyRateGross: normalizeText(normalizedTrainer?.defaultHourlyRateGross),
		certificationIds,
		certificationCount: certificationIds.length,
	}
}

let trainersStore = Array.isArray(trainersSeed)
	? trainersSeed.map(normalizeTrainer)
	: []

class TrainersService {
	constructor() {
		this.baseUrl = window.RUNTIME_CONFIG?.API_URL || ''
		this.endpoints = {
			trainers: '/api/trainers/filters',
			trainersDetails: '/api/trainers/details',
			trainersCreate: '/api/trainers',
			trainersUpdate: '/api/trainers',
			trainersDelete: '/api/trainers',
		}
	}

	async getAll(options = {}) {
		try {
			const {
				limit = 25,
				sortColumn = 'name',
				sortDirection = 'asc',
				filters = {},
				certificationIds = [],
			} = options

			const trainers = this._normalizeTrainers(trainersStore)
			const filteredTrainers = this._applyFilters(trainers, {
				...filters,
				searchText: '',
				certificationIds,
			})
			const sortedTrainers = this._applySorting(filteredTrainers, sortColumn, sortDirection)
			const paginatedTrainers = sortedTrainers.slice(0, limit)

			return {
				data: paginatedTrainers,
				error: null,
				totalCount: sortedTrainers.length,
				hasMore: paginatedTrainers.length < sortedTrainers.length,
			}
		} catch (error) {
			console.error('TrainersService.getAll error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch trainers',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async getById(trainerId) {
		try {
			if (!trainerId) {
				return {
					data: null,
					error: 'Invalid trainer ID provided',
				}
			}

			const trainer = trainersStore.find(item => String(item?._id || item?.id) === String(trainerId))
			if (!trainer) {
				return {
					data: null,
					error: 'Trainer not found',
				}
			}

			return {
				data: this._normalizeTrainers([trainer])[0] || null,
				error: null,
			}
		} catch (error) {
			console.error('TrainersService.getById error:', error)
			return {
				data: null,
				error: error.message || 'Failed to fetch trainer',
			}
		}
	}

	async search(searchTerm, options = {}) {
		try {
			const trimmed = `${searchTerm || ''}`.trim()
			if (!trimmed) {
				return {
					data: [],
					error: 'Invalid search term provided',
					totalCount: 0,
					hasMore: false,
				}
			}

			const {
				limit = 25,
				sortColumn = 'name',
				sortDirection = 'asc',
				filters = {},
				certificationIds = [],
			} = options

			const trainers = this._normalizeTrainers(trainersStore)
			const filteredTrainers = this._applyFilters(trainers, {
				...filters,
				searchText: trimmed,
				certificationIds,
			})
			const sortedTrainers = this._applySorting(filteredTrainers, sortColumn, sortDirection)
			const paginatedTrainers = sortedTrainers.slice(0, limit)

			return {
				data: paginatedTrainers,
				error: null,
				totalCount: sortedTrainers.length,
				hasMore: paginatedTrainers.length < sortedTrainers.length,
			}
		} catch (error) {
			console.error('TrainersService.search error:', error)
			return {
				data: [],
				error: error.message || 'Failed to search trainers',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async create(trainerData = {}) {
		try {
			if (!trainerData?.name) {
				return {
					success: false,
					error: 'Trainer name is required',
					data: null,
				}
			}

			const newTrainer = normalizeTrainer({
				_id: `trainer-${Date.now()}`,
				name: trainerData.name,
				city: trainerData.city,
				wojewodztwo: trainerData.wojewodztwo,
				address: trainerData.address,
				postalCode: trainerData.postalCode,
				secondaryCity: trainerData.secondaryCity,
				secondaryWojewodztwo: trainerData.secondaryWojewodztwo,
				secondaryAddress: trainerData.secondaryAddress,
				secondaryPostalCode: trainerData.secondaryPostalCode,
				activeAddressType: trainerData.activeAddressType,
				id_miasto: trainerData.id_miasto,
				id_wojewodztwo: trainerData.id_wojewodztwo,
				secondary_id_miasto: trainerData.secondary_id_miasto,
				secondary_id_wojewodztwo: trainerData.secondary_id_wojewodztwo,
				defaultHourlyRateGross: trainerData.defaultHourlyRateGross,
				certificationIds: Array.isArray(trainerData.certificationIds) ? trainerData.certificationIds.filter(Boolean) : [],
			})

			trainersStore = [newTrainer, ...trainersStore]

			return {
				success: true,
				error: null,
				data: newTrainer,
			}
		} catch (error) {
			console.error('TrainersService.create error:', error)
			return {
				success: false,
				error: error.message || 'Failed to create trainer',
				data: null,
			}
		}
	}

	async update(trainerId, trainerData = {}) {
		try {
			if (!trainerId) {
				return {
					success: false,
					error: 'Invalid trainer ID provided',
					data: null,
				}
			}

			const trainerIndex = trainersStore.findIndex(item => String(item?._id || item?.id) === String(trainerId))
			if (trainerIndex === -1) {
				return {
					success: false,
					error: 'Trainer not found',
					data: null,
				}
			}

			const updatedTrainer = {
				...normalizeTrainer(trainersStore[trainerIndex]),
				...trainerData,
				name: trainerData?.name ?? trainersStore[trainerIndex]?.name,
				city: trainerData?.city ?? trainersStore[trainerIndex]?.city,
				wojewodztwo: trainerData?.wojewodztwo ?? trainersStore[trainerIndex]?.wojewodztwo,
				address: trainerData?.address ?? trainersStore[trainerIndex]?.address,
				postalCode: trainerData?.postalCode ?? trainersStore[trainerIndex]?.postalCode,
				secondaryCity: trainerData?.secondaryCity ?? trainersStore[trainerIndex]?.secondaryCity,
				secondaryWojewodztwo: trainerData?.secondaryWojewodztwo ?? trainersStore[trainerIndex]?.secondaryWojewodztwo,
				secondaryAddress: trainerData?.secondaryAddress ?? trainersStore[trainerIndex]?.secondaryAddress,
				secondaryPostalCode: trainerData?.secondaryPostalCode ?? trainersStore[trainerIndex]?.secondaryPostalCode,
				activeAddressType: trainerData?.activeAddressType ?? trainersStore[trainerIndex]?.activeAddressType,
				id_miasto: trainerData?.id_miasto ?? trainersStore[trainerIndex]?.id_miasto,
				id_wojewodztwo: trainerData?.id_wojewodztwo ?? trainersStore[trainerIndex]?.id_wojewodztwo,
				secondary_id_miasto: trainerData?.secondary_id_miasto ?? trainersStore[trainerIndex]?.secondary_id_miasto,
				secondary_id_wojewodztwo: trainerData?.secondary_id_wojewodztwo ?? trainersStore[trainerIndex]?.secondary_id_wojewodztwo,
				defaultHourlyRateGross: trainerData?.defaultHourlyRateGross ?? trainersStore[trainerIndex]?.defaultHourlyRateGross,
				certificationIds: Array.isArray(trainerData?.certificationIds)
					? trainerData.certificationIds.filter(Boolean)
					: Array.isArray(trainersStore[trainerIndex]?.certificationIds)
					? trainersStore[trainerIndex].certificationIds
					: [],
			}
			const normalizedTrainer = normalizeTrainer(updatedTrainer)

			trainersStore = trainersStore.map(item =>
				String(item?._id || item?.id) === String(trainerId) ? normalizedTrainer : item
			)

			return {
				success: true,
				error: null,
				data: normalizedTrainer,
			}
		} catch (error) {
			console.error('TrainersService.update error:', error)
			return {
				success: false,
				error: error.message || 'Failed to update trainer',
				data: null,
			}
		}
	}

	async delete(trainerId) {
		try {
			const trainerExists = trainersStore.some(item => String(item?._id || item?.id) === String(trainerId))

			if (!trainerExists) {
				return {
					success: false,
					error: 'Trainer not found',
				}
			}

			trainersStore = trainersStore.filter(item => String(item?._id || item?.id) !== String(trainerId))

			return {
				success: true,
				error: null,
			}
		} catch (error) {
			console.error('TrainersService.delete error:', error)
			return {
				success: false,
				error: error.message || 'Failed to delete trainer',
			}
		}
	}

	_normalizeTrainers(trainers = []) {
		return (Array.isArray(trainers) ? trainers : []).map(normalizeTrainer)
	}

	_applyFilters(trainers, filters = {}) {
		const searchText = `${filters?.searchText || ''}`.trim().toLowerCase()
		const certificationIds = Array.isArray(filters?.certificationIds) ? filters.certificationIds.filter(Boolean) : []
		const selectedMiastoId = `${filters?.id_miasto || ''}`.trim()
		const selectedMiastoName = `${filters?.miastoName || ''}`.trim().toLowerCase()
		const selectedWojewodztwoId = `${filters?.id_wojewodztwo || ''}`.trim()
		const selectedWojewodztwoName = `${filters?.wojewodztwoName || ''}`.trim().toLowerCase()

		return (Array.isArray(trainers) ? trainers : []).filter(trainer => {
			const matchesSearch = !searchText
				|| `${trainer?.name || ''}`.toLowerCase().includes(searchText)
				|| `${trainer?.city || ''}`.toLowerCase().includes(searchText)
				|| `${trainer?.wojewodztwo || ''}`.toLowerCase().includes(searchText)
				|| `${trainer?.address || ''}`.toLowerCase().includes(searchText)
				|| `${trainer?.postalCode || ''}`.toLowerCase().includes(searchText)
				|| `${trainer?.secondaryCity || ''}`.toLowerCase().includes(searchText)
				|| `${trainer?.secondaryWojewodztwo || ''}`.toLowerCase().includes(searchText)
				|| `${trainer?.secondaryAddress || ''}`.toLowerCase().includes(searchText)
				|| `${trainer?.secondaryPostalCode || ''}`.toLowerCase().includes(searchText)
				|| `${trainer?.activeCity || ''}`.toLowerCase().includes(searchText)
				|| `${trainer?.activeWojewodztwo || ''}`.toLowerCase().includes(searchText)
				|| `${trainer?.activeAddress || ''}`.toLowerCase().includes(searchText)
				|| `${trainer?.activePostalCode || ''}`.toLowerCase().includes(searchText)

			const matchesMiasto = !selectedMiastoId
				|| `${trainer?.active_id_miasto || ''}` === selectedMiastoId
				|| (!!selectedMiastoName && `${trainer?.activeCity || ''}`.toLowerCase() === selectedMiastoName)

			const matchesWojewodztwo = !selectedWojewodztwoId
				|| `${trainer?.active_id_wojewodztwo || ''}` === selectedWojewodztwoId
				|| (!!selectedWojewodztwoName && `${trainer?.activeWojewodztwo || ''}`.toLowerCase() === selectedWojewodztwoName)

			const matchesCertifications = !certificationIds.length
				|| certificationIds.every(certificationId =>
					Array.isArray(trainer?.certificationIds) && trainer.certificationIds.includes(certificationId)
				)

			return matchesSearch && matchesMiasto && matchesWojewodztwo && matchesCertifications
		})
	}

	_applySorting(trainers, sortColumn = 'name', sortDirection = 'asc') {
		const direction = sortDirection === 'desc' ? -1 : 1

		return [...(Array.isArray(trainers) ? trainers : [])].sort((left, right) => {
			if (sortColumn === 'defaultHourlyRateGross' || sortColumn === 'certificationCount') {
				const leftValue = Number(`${left?.[sortColumn] ?? ''}`.replace(',', '.')) || 0
				const rightValue = Number(`${right?.[sortColumn] ?? ''}`.replace(',', '.')) || 0

				if (leftValue < rightValue) return -1 * direction
				if (leftValue > rightValue) return 1 * direction
				return 0
			}

			const leftValue = `${this._getSortValue(left, sortColumn) || ''}`.toLowerCase()
			const rightValue = `${this._getSortValue(right, sortColumn) || ''}`.toLowerCase()

			return leftValue.localeCompare(rightValue, 'pl') * direction
		})
	}

	_getSortValue(trainer, sortColumn) {
		if (sortColumn === 'city') return trainer?.activeCity || trainer?.city
		if (sortColumn === 'wojewodztwo') return trainer?.activeWojewodztwo || trainer?.wojewodztwo
		if (sortColumn === 'address') return trainer?.activeAddress || trainer?.address
		if (sortColumn === 'postalCode') return trainer?.activePostalCode || trainer?.postalCode
		return trainer?.[sortColumn]
	}
}

export default new TrainersService()
